import { defineComponent as _defineComponent } from 'vue'
import { vModelDynamic as _vModelDynamic, withKeys as _withKeys, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-534ea3bc"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = ["id", "name", "autocomplete", "type", "placeholder", "autofocus", "maxlength", "data-testid"]
const _hoisted_3 = { class: "right-element" }

import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  InputValidator,
  InputFormatter,
  InputError,
  InputSize,
} from '@/popup/components/shared/Form/FormTypes'

type Props = {
  value?: string
  errors?: InputError[]
  type?: string
  placeholder?: string
  size?: InputSize
  validators?: InputValidator[]
  validateOnBlur?: boolean
  validateOnLoad?: boolean
  autofocus?: boolean
  formatters?: InputFormatter[]
  maxlength?: number
  isRequired?: boolean
  name?: string
  isBordered?: boolean
  extraClasses?: string
  autocomplete?: string
}

type Emits = {
  (event: 'on-enter', value: string): void
  (event: 'on-update', value: string): void
  (event: 'on-errors', errors: InputError[]): void
  (event: 'on-blur', value: string): void
  (event: 'on-key-press', value: KeyboardEvent): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Input',
  props: {
    value: { default: '' },
    errors: { default: undefined },
    type: { default: 'text' },
    placeholder: { default: '' },
    size: { default: 'medium' },
    validators: { default: undefined },
    validateOnBlur: { type: Boolean, default: true },
    validateOnLoad: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    formatters: { default: undefined },
    maxlength: { default: undefined },
    isRequired: { type: Boolean, default: false },
    name: { default: '' },
    isBordered: { type: Boolean, default: false },
    extraClasses: { default: '' },
    autocomplete: { default: undefined }
  },
  emits: ["on-enter", "on-update", "on-errors", "on-blur", "on-key-press"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props
const emits = __emit

const touched = ref(false)
const input = ref(props.value)
const errors = ref(props.errors)
const element = ref<HTMLInputElement>()
const { t } = useI18n({ useScope: 'global' })

const enter = () => {
  format(input.value)
  validate(input.value)

  emits('on-enter', input.value)
}

const keyPress = (event: KeyboardEvent) => {
  emits('on-key-press', event)
}

const update = () => {
  format(input.value)

  if (!props.validateOnBlur || touched.value) {
    validate(input.value)
  }

  emits('on-update', input.value)
}

const validate = (value: string) => {
  let validationError: InputError[] = []

  if (value && props.validators) {
    validationError = props.validators
      ?.map(validator => validator(value, element.value))
      .filter(Boolean)
  }

  errors.value = validationError

  if (props.isRequired && !!!value) {
    errors.value.push(t('input.validators.required'))
  }
  emits('on-errors', errors.value || [])
}

__expose({ validate })

const format = (value: string) => {
  if (value && props.formatters)
    for (const formatter of props.formatters) input.value = formatter(value)
}

watch(
  () => ({ value: props.autofocus }),
  ({ value: newValue }) => {
    if (newValue && !touched.value) {
      element.value?.focus()
    }
  }
)

watch(
  () => ({ value: props.value, errors: props.errors }),
  ({ value: newValue, errors: newErrors }, { value: oldValue }) => {
    if (newValue !== oldValue) {
      input.value = newValue
      if (props.validateOnLoad) {
        validate(input.value)
      }
    }

    errors.value = newErrors
  }
)

const blur = () => {
  emits('on-blur', input.value)
  touched.value = true

  if (props.validateOnBlur) {
    validate(input.value)
  }
}

const hasErrors = computed(() => {
  return !!errors.value?.length
})

onMounted(() => {
  if (props.autofocus) {
    element.value?.focus()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives(_createElementVNode("input", {
      ref_key: "element",
      ref: element,
      id: _ctx.name,
      name: _ctx.name,
      autocomplete: _ctx.autocomplete,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((input).value = $event)),
      class: _normalizeClass(`is-${_ctx.size} ${hasErrors.value ? 'is-danger' : ''} ${
        _ctx.isBordered ? 'is-bordered' : ''
      } ${_ctx.extraClasses}`),
      type: _ctx.type,
      placeholder: _ctx.placeholder,
      autofocus: _ctx.autofocus,
      maxlength: _ctx.maxlength,
      "data-testid": _ctx.name ? `input-${_ctx.name}` : 'input',
      onKeypress: [
        _withKeys(enter, ["enter"]),
        keyPress
      ],
      onInput: update,
      onBlur: blur
    }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_2), [
      [_vModelDynamic, input.value]
    ]),
    _createElementVNode("div", _hoisted_3, [
      _renderSlot(_ctx.$slots, "default")
    ])
  ]))
}
}

})